import React, { useMemo } from 'react';
import { Heading, Stack } from '@global-ecom/nitro-uds/elements';

import { Page } from 'ui/components/Page';
import { PageHead } from 'ui/components/PageHead';
import { SanityContentSlot } from 'ui/components/SanityContentSlot';
import { ComponentWithFilters, Seo } from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { SUPPORTED_COUNTRY } from 'utils/constants';
import {
  LaunchCalendarItem,
  LaunchCalendarList,
} from 'ui/components/LaunchCalendar/LaunchCalendarList';
import { useContentSplitByMonth } from 'hooks/useContentSplitByMonth';
import { useItemListViewEvent } from 'hooks/useItemListViewEvent';
import { transformListProductToAnalyticsItem } from 'utils/analytics';

type LaunchCalendarPage = {
  _id: string;
  _type: string;
  seo: Seo;
  aboveContent?: ComponentWithFilters[];
  belowContent?: ComponentWithFilters[];
};

type LaunchCalenderPageProps = {
  page: LaunchCalendarPage;
  items: LaunchCalendarItem[];
};

export const LaunchCalendarPage = ({
  page,
  items,
}: LaunchCalenderPageProps) => {
  const t = useTranslate();
  const { country } = useSiteConfig();

  const releasedItems = items.filter(item => item.released);
  releasedItems.sort((a, b) => b.launchDate.localeCompare(a.launchDate));

  const futureItemsByMonth = useContentSplitByMonth(items);

  const { currency } = useSiteConfig();
  const listItems = useMemo(() => {
    if (!items) return [];
    return items.map((product, index) => {
      return transformListProductToAnalyticsItem({
        product,
        currency: currency.code,
        quantity: 1,
        categories: {
          item_category: undefined,
        },
        itemListId: page._id,
        itemListName: page._type,
        index,
      });
    });
  }, [items, currency.code, page._id, page._type]);

  const itemListId = page._id;
  const itemListName = page._type;

  useItemListViewEvent({
    itemListId,
    itemListName,
    items: listItems,
  });

  return (
    <>
      <PageHead
        title={page?.seo?.pageTitle}
        description={`${page?.seo?.pageDescription}`}
      />
      <Page>
        <SanityContentSlot
          items={page?.aboveContent}
          documentId={page?._id}
          slotId="aboveDropCalendarContent"
        />
        <Stack gap="2xl" asChild>
          <section>
            <Heading srOnly asChild>
              <h2>Launch Calendar</h2>
            </Heading>
            {futureItemsByMonth.map((group, idx) => (
              <LaunchCalendarList
                key={idx}
                items={group.items}
                title={
                  country === SUPPORTED_COUNTRY.JP
                    ? group.year + '年 ' + t(group.longMonth) + '月'
                    : t(group.longMonth) + ' ' + group.year
                }
                itemListId={itemListId}
                itemlistName={itemListName}
              />
            ))}

            {releasedItems.length > 0 && (
              <LaunchCalendarList
                items={releasedItems}
                title={t('outNow')}
                itemListId={itemListId}
                itemlistName={itemListName}
              />
            )}
          </section>
        </Stack>
        <SanityContentSlot
          items={page?.belowContent}
          documentId={page?._id}
          slotId="belowDropCalendarContent"
        />
      </Page>
    </>
  );
};
